import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText,
  CircularProgress,
  Alert,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Plus,
  Users,
  KeyRound,
  ShieldPlus,
  Building2,
  Power,
  PowerOff,
  Lock,
  X,
  Search,
} from 'lucide-react';
import platformAdminService from '../../services/platformAdminService';
import ApiKeyRevealDialog from '../shared/ApiKeyRevealDialog';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { ROLE_NAMES } from '../../constants/permissions';
import { PARTNER_ADMIN_COPY, ROLE_COPY } from '../../constants/partnerCopy';
import { mapApiError } from '../../utils/apiError';
import { EMPTY_PARTNER_INDEX, companyLabel, isHomeCompany } from '../../utils/tenancy';
import PaginationBar from './PaginationBar';
import styles from './PlatformAdmin.module.scss';

const NO_COMPANY = '__none__';
const ALL_COMPANIES = '';
const PAGE_SIZE = 50;

const fullName = (u) =>
  [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || `User ${u.id}`;

// Friendly badges for the two admin roles; everything else shows its name.
const roleDisplayName = (name) =>
  name === ROLE_NAMES.PARTNER_ADMIN
    ? ROLE_COPY.partnerAdmin
    : name === ROLE_NAMES.COMPANY_ADMIN
    ? ROLE_COPY.companyAdmin
    : name;

// Backend admin rules (Partner tier guide §1.3 / §2) → friendlier copy.
const USER_ERRORS = [
  { status: 400, match: /partner_admin can only be held/i, message: PARTNER_ADMIN_COPY.onlyDirectUsersPartnerAdmin },
  { status: 400, match: /administered with partner_admin, not company_admin/i, message: PARTNER_ADMIN_COPY.directUsersNotCompanyAdmin },
  { status: 403, match: /higher privileges/i, message: PARTNER_ADMIN_COPY.higherTierUser },
];

/**
 * Cross-company Users panel (spec §4.2). This is the only place company_admin can
 * be assigned/removed.
 *
 * Partner tier (§4.6/§4.8): a Partner filter; a partner's home company shows as
 * "<Partner> · direct users"; direct users are offered partner_admin (never
 * company_admin) and client-company users the reverse.
 */
const UsersPanel = ({ notify, partnerIndex = EMPTY_PARTNER_INDEX, partners = null }) => {
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companyFilter, setCompanyFilter] = useState(ALL_COMPANIES);
  const [partnerFilter, setPartnerFilter] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 400);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  // Create user
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    platform: '',
    company_id: NO_COMPANY,
    role_ids: [],
  });
  const [creating, setCreating] = useState(false);

  // One-time api key
  const [keyReveal, setKeyReveal] = useState(null); // { apiKey, roles, title, subtitle }

  // Assign role / move company / confirm
  const [roleTarget, setRoleTarget] = useState(null); // user
  const [roleToAdd, setRoleToAdd] = useState('');
  const [moveTarget, setMoveTarget] = useState(null); // user
  const [moveTo, setMoveTo] = useState(NO_COMPANY);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const companyName = useCallback(
    (id) => {
      if (!id) return '—';
      const name = companies.find((c) => c.id === id)?.name || `Company ${id}`;
      return companyLabel(id, name, { partnerIndex });
    },
    [companies, partnerIndex]
  );

  const companyById = useCallback((id) => companies.find((c) => c.id === id) || null, [companies]);

  const isHome = useCallback(
    (id) => !!id && id !== NO_COMPANY && isHomeCompany(id, { partnerIndex, name: companyById(id)?.name }),
    [partnerIndex, companyById]
  );

  // Users of a partner (direct users or a client company) always belong to one
  // of its companies — the backend refuses "no company" for them.
  const isPartnerUser = useCallback(
    (id) => isHome(id) || companyById(id)?.partner_id != null,
    [isHome, companyById]
  );

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await platformAdminService.listUsers(companyFilter || null, {
        partnerId: partnerFilter || null,
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setUsers(data?.users || []);
      setTotal(Number.isFinite(data?.total) ? data.total : (data?.users || []).length);
    } catch (err) {
      setError(err?.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [companyFilter, partnerFilter, debouncedSearch, offset]);

  const loadMeta = useCallback(async () => {
    try {
      // Companies + roles feed the filter/pickers — ask for a big page (max 200).
      const [companyData, roleData] = await Promise.all([
        platformAdminService.listCompanies(false, { limit: 200 }),
        platformAdminService.listRoles({ includePlatformTier: true, limit: 200 }),
      ]);
      setCompanies(companyData?.companies || (Array.isArray(companyData) ? companyData : []));
      setRoles(Array.isArray(roleData) ? roleData : roleData?.roles || []);
    } catch (err) {
      notify(err?.message || 'Failed to load companies/roles.', 'error');
    }
  }, [notify]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Roles grantable for a given company scope. NOTE: company_member is included
  // here so it can be RE-ASSIGNED to a user who had it removed; it's only filtered
  // out of the create-user picker below (new users get it automatically).
  // Partner tier: partner_admin goes only to direct users (home company) and
  // company_admin never does (§1.3).
  const rolesForCompany = useCallback(
    (companyId) => {
      const isCompanyScoped = companyId && companyId !== NO_COMPANY;
      const home = isHome(companyId);
      return roles.filter((r) => {
        if (r.name === ROLE_NAMES.PARTNER_ADMIN) return home;
        if (isCompanyScoped) {
          if (home && r.name === ROLE_NAMES.COMPANY_ADMIN) return false;
          return r.scope === 'company' && (r.company_id == null || r.company_id === companyId);
        }
        return r.scope === 'platform' && r.company_id == null;
      });
    },
    [roles, isHome]
  );

  // Assign picker: everything in scope the user doesn't already hold (incl.
  // company_member if it was removed).
  const assignableRoles = useMemo(() => {
    if (!roleTarget) return [];
    const held = new Set((roleTarget.roles || []).map((r) => r.id));
    return rolesForCompany(roleTarget.company_id || NO_COMPANY).filter((r) => !held.has(r.id));
  }, [roleTarget, rolesForCompany]);

  // Create picker: exclude company_member — the backend assigns it automatically.
  const createRoleOptions = useMemo(
    () => rolesForCompany(form.company_id).filter((r) => r.name !== 'company_member'),
    [rolesForCompany, form.company_id]
  );

  // ── Create ──
  const resetForm = () =>
    setForm({ email: '', first_name: '', last_name: '', platform: '', company_id: NO_COMPANY, role_ids: [] });

  const handleCreate = async () => {
    if (!form.email.trim()) return;
    try {
      setCreating(true);
      const payload = {
        email: form.email.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        platform: form.platform.trim(),
        role_ids: form.role_ids,
      };
      if (form.company_id && form.company_id !== NO_COMPANY) payload.company_id = form.company_id;
      const res = await platformAdminService.createUser(payload);
      setCreateOpen(false);
      resetForm();
      setKeyReveal({
        apiKey: res?.api_key,
        roles: res?.roles || [],
        title: 'User created',
        subtitle: `Copy ${res?.user?.email || 'the user'}'s API key now — it will not be shown again.`,
      });
      await loadUsers();
    } catch (err) {
      notify(mapApiError(err, USER_ERRORS, 'Failed to create user.'), 'error');
    } finally {
      setCreating(false);
    }
  };

  // ── Row actions ──
  const toggleActive = (user) => {
    if (user.is_active) {
      setConfirm({
        type: 'deactivate',
        user,
        heading: `Deactivate ${user.email}?`,
        text: 'The user is signed out immediately and cannot log in until reactivated.',
        action: 'Deactivate',
      });
    } else {
      runUpdate(user.id, { is_active: true }, 'User reactivated.');
    }
  };

  const runUpdate = async (userId, patch, successMsg) => {
    try {
      setBusy(true);
      await platformAdminService.updateUser(userId, patch);
      notify(successMsg, 'success');
      setConfirm(null);
      setMoveTarget(null);
      await loadUsers();
    } catch (err) {
      notify(mapApiError(err, USER_ERRORS, 'Update failed.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleAssignRole = async () => {
    if (!roleTarget || !roleToAdd) return;
    try {
      setBusy(true);
      await platformAdminService.assignUserRole(roleTarget.id, roleToAdd);
      notify('Role assigned.', 'success');
      setRoleTarget(null);
      setRoleToAdd('');
      await loadUsers();
    } catch (err) {
      notify(mapApiError(err, USER_ERRORS, 'Failed to assign role.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveRole = async () => {
    if (confirm?.type !== 'removeRole') return;
    try {
      setBusy(true);
      await platformAdminService.removeUserRole(confirm.user.id, confirm.roleId);
      notify('Role removed.', 'info');
      setConfirm(null);
      await loadUsers();
    } catch (err) {
      notify(mapApiError(err, USER_ERRORS, 'Failed to remove role.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleMove = async () => {
    if (!moveTarget) return;
    const patch =
      moveTo === NO_COMPANY ? { clear_company: true } : { company_id: moveTo };
    await runUpdate(moveTarget.id, patch, 'User moved.');
  };

  const handleResetKey = async () => {
    if (confirm?.type !== 'resetKey') return;
    try {
      setBusy(true);
      const res = await platformAdminService.resetUserApiKey(confirm.user.id);
      setConfirm(null);
      setKeyReveal({
        apiKey: res?.api_key,
        title: 'API key reset',
        subtitle: `The old key for ${confirm.user.email} stopped working immediately. Copy the new key now.`,
      });
    } catch (err) {
      notify(mapApiError(err, USER_ERRORS, 'Failed to reset key.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const runConfirm = () => {
    if (confirm?.type === 'deactivate') runUpdate(confirm.user.id, { is_active: false }, 'User deactivated.');
    else if (confirm?.type === 'removeRole') handleRemoveRole();
    else if (confirm?.type === 'resetKey') handleResetKey();
  };

  return (
    <>
      <Box className={styles.toolbar}>
        <Box className={styles.filterGroup}>
          <TextField
            size="small"
            placeholder="Search email or name…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
            sx={{ minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
          />
          {partners && partners.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Partner</InputLabel>
              <Select
                label="Partner"
                value={partnerFilter}
                onChange={(e) => { setPartnerFilter(e.target.value); setOffset(0); }}
              >
                <MenuItem value="">All partners</MenuItem>
                {partners.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Company</InputLabel>
            <Select
              label="Company"
              value={companyFilter}
              onChange={(e) => { setCompanyFilter(e.target.value); setOffset(0); }}
            >
              <MenuItem value={ALL_COMPANIES}>All companies</MenuItem>
              {companies.map((c) => (
                <MenuItem key={c.id} value={c.id}>{companyName(c.id)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          className={styles.primaryBtn}
          onClick={() => setCreateOpen(true)}
        >
          New user
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ borderRadius: '12px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : users.length === 0 ? (
        <Box className={styles.emptyState}>
          <Users className={styles.emptyIcon} size={56} />
          <Typography variant="h6">No users found</Typography>
          <Typography variant="body2">Create a user or pick a different company filter.</Typography>
        </Box>
      ) : (
        <Box className={styles.tableCard}>
          {users.map((user) => (
            <Box
              key={user.id}
              className={styles.userRow}
              sx={{ flexWrap: 'wrap' }}
            >
              <Box className={styles.companyMain} sx={{ flex: '1 1 220px' }}>
                <span className={styles.companyAvatar}>
                  {(user.first_name || user.email || '?').charAt(0).toUpperCase()}
                </span>
                <Box>
                  <span className={styles.companyName}>{fullName(user)}</span>
                  <span className={styles.companyMeta}>{user.email}</span>
                </Box>
              </Box>

              <Box sx={{ flex: '0 0 auto', minWidth: 120 }}>
                <span className={styles.badge}>
                  <Building2 size={12} />
                  {companyName(user.company_id)}
                </span>
              </Box>

              <Box className={styles.roleChips} sx={{ flex: '1 1 200px' }}>
                {(user.roles || []).map((r) => (
                  <span
                    key={r.id}
                    className={`${styles.badge} ${
                      r.name === ROLE_NAMES.COMPANY_ADMIN || r.name === ROLE_NAMES.PARTNER_ADMIN
                        ? styles.badgeAdmin
                        : r.is_system
                        ? styles.badgeSystem
                        : ''
                    }`}
                  >
                    {roleDisplayName(r.name)}
                    {/* Platform admins may remove ANY role, including the default
                        company_member and company_admin (spec §4.2). */}
                    <X
                      size={12}
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        setConfirm({
                          type: 'removeRole',
                          user,
                          roleId: r.id,
                          heading: `Remove role "${r.name}"?`,
                          text:
                            r.name === 'company_member'
                              ? `Removes the default company_member role from ${user.email}. Their access becomes just their other roles ∩ the company's entitlements.`
                              : `Removes the ${r.name} role from ${user.email}.`,
                          action: 'Remove role',
                        })
                      }
                    />
                  </span>
                ))}
              </Box>

              <span className={`${styles.statusPill} ${user.is_active ? styles.active : styles.inactive}`}>
                {user.is_active ? 'Active' : 'Inactive'}
              </span>

              <Box className={styles.actionsCell}>
                <Tooltip title="Assign role">
                  <IconButton size="small" className={styles.subtleBtn} onClick={() => { setRoleTarget(user); setRoleToAdd(''); }}>
                    <ShieldPlus size={16} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Move company">
                  <IconButton size="small" className={styles.subtleBtn} onClick={() => { setMoveTarget(user); setMoveTo(user.company_id || NO_COMPANY); }}>
                    <Building2 size={16} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reset API key">
                  <IconButton
                    size="small"
                    className={styles.subtleBtn}
                    onClick={() =>
                      setConfirm({
                        type: 'resetKey',
                        user,
                        heading: `Reset API key for ${user.email}?`,
                        text: 'The current key stops working immediately. The new key is shown once.',
                        action: 'Reset key',
                      })
                    }
                  >
                    <KeyRound size={16} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={user.is_active ? 'Deactivate' : 'Activate'}>
                  <IconButton size="small" className={styles.dangerIcon} onClick={() => toggleActive(user)}>
                    {user.is_active ? <PowerOff size={16} /> : <Power size={16} />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {total > 0 && (
        <PaginationBar
          total={total}
          limit={PAGE_SIZE}
          offset={offset}
          loading={loading}
          unit="users"
          onOffsetChange={setOffset}
        />
      )}

      {/* Create user */}
      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>New user</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <TextField
            autoFocus label="Email" type="email" required fullWidth variant="outlined"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={styles.formField}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="First name" fullWidth variant="outlined"
              value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              className={styles.formField}
            />
            <TextField
              label="Last name" fullWidth variant="outlined"
              value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              className={styles.formField}
            />
          </Box>
          <FormControl fullWidth size="medium">
            <InputLabel>Company</InputLabel>
            <Select
              label="Company"
              value={form.company_id}
              onChange={(e) => setForm({ ...form, company_id: e.target.value, role_ids: [] })}
            >
              <MenuItem value={NO_COMPANY}>No company (platform user)</MenuItem>
              {companies.map((c) => (
                <MenuItem key={c.id} value={c.id}>{companyName(c.id)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="medium">
            <InputLabel>Extra roles</InputLabel>
            <Select
              multiple
              label="Extra roles"
              value={form.role_ids}
              onChange={(e) => setForm({ ...form, role_ids: e.target.value })}
              input={<OutlinedInput label="Extra roles" />}
              renderValue={(selected) =>
                createRoleOptions.filter((r) => selected.includes(r.id)).map((r) => r.name).join(', ')
              }
            >
              {createRoleOptions.length === 0 && (
                <MenuItem disabled>No extra roles available</MenuItem>
              )}
              {createRoleOptions.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  <Checkbox checked={form.role_ids.includes(r.id)} />
                  <ListItemText primary={roleDisplayName(r.name)} secondary={r.scope} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            company_member is assigned automatically. company_admin can be granted here (platform only).
            A partner's direct users get Partner admin instead of Company admin.
          </Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setCreateOpen(false)} disabled={creating} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating || !form.email.trim()} className={styles.primaryBtn}>
            {creating ? <CircularProgress size={20} color="inherit" /> : 'Create user'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign role */}
      <Dialog open={!!roleTarget} onClose={() => !busy && setRoleTarget(null)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>Assign role</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Grant a role to {roleTarget?.email}.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select label="Role" value={roleToAdd} onChange={(e) => setRoleToAdd(e.target.value)}>
              {assignableRoles.length === 0 && <MenuItem disabled>No roles available to add</MenuItem>}
              {assignableRoles.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {roleDisplayName(r.name)}{r.is_system ? ' (system)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setRoleTarget(null)} disabled={busy} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={handleAssignRole} disabled={busy || !roleToAdd} className={styles.primaryBtn}>
            {busy ? <CircularProgress size={20} color="inherit" /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move company */}
      <Dialog open={!!moveTarget} onClose={() => !busy && setMoveTarget(null)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>Move company</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Moving {moveTarget?.email} to another company removes their company admin role and the
            old company's custom roles.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Company</InputLabel>
            <Select label="Company" value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
              {!isPartnerUser(moveTarget?.company_id) && (
                <MenuItem value={NO_COMPANY}>Detach (no company)</MenuItem>
              )}
              {companies.map((c) => (
                <MenuItem key={c.id} value={c.id}>{companyName(c.id)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setMoveTarget(null)} disabled={busy} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={handleMove} disabled={busy} className={styles.primaryBtn}>
            {busy ? <CircularProgress size={20} color="inherit" /> : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generic confirm (deactivate / remove role / reset key) */}
      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>{confirm?.action}</DialogTitle>
        <DialogContent className={styles.deleteContent}>
          <Box className={styles.warningIconWrap}><Lock /></Box>
          <Typography className={styles.deleteHeading}>{confirm?.heading}</Typography>
          <Typography className={styles.deleteText}>{confirm?.text}</Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setConfirm(null)} disabled={busy} className={styles.cancelButton}>Cancel</Button>
          <Button
            variant="contained"
            onClick={runConfirm}
            disabled={busy}
            className={styles.dangerButton}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {busy ? 'Working...' : confirm?.action}
          </Button>
        </DialogActions>
      </Dialog>

      <ApiKeyRevealDialog
        open={!!keyReveal}
        apiKey={keyReveal?.apiKey}
        roles={keyReveal?.roles}
        title={keyReveal?.title}
        subtitle={keyReveal?.subtitle}
        onClose={() => setKeyReveal(null)}
      />
    </>
  );
};

export default UsersPanel;
